import { useState, useEffect, useRef } from "react";
import { toDateFromApi } from "../../helpers/dateTimeFormat";
import { useBusinessTimezone } from "../../context/BusinessTimezone/BusinessTimezoneContext";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";

import { getHoursCloseTicketsAuto } from "../../config";

const useTickets = ({
	searchParam,
	pageNumber,
	status,
	date,
	showAll,
	showUnread,
	queueIds,
	withUnreadMessages,
	unreadBadge,
	countOnly,
	unreadMessagesSum,
	badgeRefresh,
	period,
	tagsIds,
	channels,
	reloadTicktList
}) => {
	const [loading, setLoading] = useState(true);
	const [hasMore, setHasMore] = useState(false);
	const [tickets, setTickets] = useState([]);
	const [count, setCount] = useState(0);
	const { timeZone: businessTimeZone } = useBusinessTimezone();
	const requestIdRef = useRef(0);

	const isAggregateOnly =
		countOnly === "true" ||
		unreadMessagesSum === "true" ||
		unreadBadge === "true";

	useEffect(() => {
		setLoading(true);
		const debounceMs = isAggregateOnly ? 400 : 500;
		const requestId = ++requestIdRef.current;
		const controller = typeof AbortController !== "undefined" ? new AbortController() : null;

		const delayDebounceFn = setTimeout(() => {
			const fetchTickets = async () => {
				try {
					const params = {
						searchParam,
						pageNumber,
						status,
						date,
						showAll:
							showAll === true || showAll === "true" ? "true" : showAll,
						showUnread,
						queueIds,
						withUnreadMessages,
						unreadBadge,
						countOnly,
						unreadMessagesSum,
						tagsIds,
						channels
					};
					status === "closed" && (params.period = period);
					const { data } = await apiV2.get("/reloadTickets", {
						params,
						signal: controller?.signal,
					});

					if (requestId !== requestIdRef.current) return;

					setTickets(Array.isArray(data.tickets) ? data.tickets : []);
					setHasMore(Boolean(data.hasMore));
					setCount(data.count);

					// Contadores/agregados não devem disparar fechamento automático
					// (isso gerava cascata de PUT + socket + mais reloadTickets).
					if (!isAggregateOnly && withUnreadMessages !== "true") {
						await maybeAutoCloseTickets(data.tickets || [], status, businessTimeZone);
					}

					if (requestId !== requestIdRef.current) return;
					setLoading(false);
				} catch (err) {
					if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError") {
						return;
					}
					if (requestId !== requestIdRef.current) return;
					setLoading(false);
					toastError(err);
				}
			};

			fetchTickets();
		}, debounceMs);

		return () => {
			clearTimeout(delayDebounceFn);
			controller?.abort();
		};
	}, [
		searchParam,
		pageNumber,
		status,
		date,
		showAll,
		showUnread,
		queueIds,
		withUnreadMessages,
		unreadBadge,
		countOnly,
		unreadMessagesSum,
		badgeRefresh,
		period,
		tagsIds,
		businessTimeZone,
		channels,
		reloadTicktList,
		isAggregateOnly,
	]);

	return {
		tickets,
		loading,
		hasMore,
		count,
	};
};

async function maybeAutoCloseTickets(tickets, status, businessTimeZone) {
	if (!Array.isArray(tickets) || tickets.length === 0) return;

	const closeTicket = async (ticket) => {
		await apiV2.put(`/tickets/${ticket.id}`, {
			status: "closed",
			userId: ticket.userId || null,
		});
	};

	const dataLimiteStatus = new Date(Date.now() - 24 * 60 * 60 * 1000);
	const statusClosures = [];

	for (const ticket of tickets) {
		if (ticket.status === "closed" || !ticket.isStatus || !ticket.updatedAt) continue;
		const dataStatusAtualizado = toDateFromApi(ticket.updatedAt, businessTimeZone);
		if (!dataStatusAtualizado) continue;
		if (dataStatusAtualizado < dataLimiteStatus) {
			statusClosures.push(closeTicket(ticket));
		}
	}

	const horasFecharAutomaticamente = getHoursCloseTicketsAuto();
	const autoCloseHours = Number(horasFecharAutomaticamente);
	const openClosures = [];

	if (
		status === "open" &&
		horasFecharAutomaticamente &&
		horasFecharAutomaticamente !== "" &&
		horasFecharAutomaticamente !== "0" &&
		autoCloseHours > 0
	) {
		const dataLimite = new Date(Date.now() - autoCloseHours * 60 * 60 * 1000);
		for (const ticket of tickets) {
			if (ticket.status === "closed") continue;
			const dataUltimaInteracaoChamado = toDateFromApi(ticket.updatedAt, businessTimeZone);
			if (!dataUltimaInteracaoChamado) continue;
			if (dataUltimaInteracaoChamado < dataLimite) {
				openClosures.push(closeTicket(ticket));
			}
		}
	}

	const jobs = [...statusClosures, ...openClosures].slice(0, 5);
	if (jobs.length) {
		await Promise.allSettled(jobs);
	}
}

export default useTickets;
